<template>
  <div
    class="nav-card"
    :class="{ 'is-link': hasLink }"
    @click="linkToPage"
  >
    <div class="card-top">
      <div
        class="monogram"
        :style="{ background: monogramBg }"
      >
        {{ initial }}
      </div>

      <div class="card-info">
        <div
          class="card-name"
          :title="data.name"
        >
          {{ data.name }}
        </div>
        <div class="card-host">
          {{ host }}
        </div>
      </div>

      <el-popover
        v-if="data.qrcode"
        placement="top-end"
        trigger="hover"
        popper-class="qrcode-popover"
      >
        <el-image
          class="qrcode-large"
          :src="data.qrcode"
        />
        <div
          slot="reference"
          class="qrcode-thumb"
          @click.stop
        >
          <img
            :src="data.qrcode"
            alt="二维码"
          >
        </div>
      </el-popover>
    </div>

    <div class="card-desc">
      {{ data.description }}
    </div>

    <div class="card-actions">
      <span
        v-if="hasLink"
        class="visit-tip"
      >
        立即访问
        <i class="el-icon-right" />
      </span>
      <span
        v-else
        class="visit-tip visit-tip--muted"
      >
        <i class="el-icon-full-screen" />
        扫码使用
      </span>

      <i
        v-if="hasLink"
        class="el-icon-document-copy copy-btn"
        title="复制链接"
        @click.stop="copyUrl"
      />
    </div>
  </div>
</template>

<script>
  import Clipboard from 'clipboard'

  const MONOGRAM_COLORS = [
    'linear-gradient(135deg, #ff8a5c, #f5484d)',
    'linear-gradient(135deg, #4facfe, #2f6fed)',
    'linear-gradient(135deg, #34d0c3, #1795a8)',
    'linear-gradient(135deg, #b06cf5, #7345e6)',
    'linear-gradient(135deg, #5ad06e, #2e9e52)',
    'linear-gradient(135deg, #f7b733, #e08a1e)',
  ]

  export default {
    props: {
      data: {
        type: Object,
        default: () => ({}),
      },
    },
    computed: {
      hasLink() {
        const url = this.data.url || ''
        return url !== '' && url !== '/#'
      },
      initial() {
        const name = (this.data.name || '')
          .replace(/[【】\[\]()（）\s]/g, '')
        return (name[0] || '?').toUpperCase()
      },
      monogramBg() {
        const name = this.data.name || ''
        let hash = 0
        for (let i = 0; i < name.length; i++) {
          hash += name.charCodeAt(i)
        }
        return MONOGRAM_COLORS[hash % MONOGRAM_COLORS.length]
      },
      host() {
        if (!this.hasLink) return '仅限扫码访问'
        try {
          return new URL(this.data.url).host
        } catch (e) {
          return this.data.url
        }
      },
    },
    methods: {
      linkToPage() {
        if (this.hasLink) {
          window.open(this.data.url)
        }
      },
      copyUrl(e) {
        const clipboard = new Clipboard(e.target, {
          text: () => this.data.url,
        })
        clipboard.on('success', () => {
          this.$message({ message: '链接已复制', type: 'success' })
          clipboard.destroy()
        })
        clipboard.on('error', () => {
          this.$message({ message: '复制失败', type: 'error' })
          clipboard.destroy()
        })
        // clipboard.js 需要在本次点击事件内触发，否则首次点击不生效
        clipboard.onClick(e)
      },
    },
  }
</script>

<style lang="scss" scoped>
  .nav-card {
    display: flex;
    flex-direction: column;
    padding: 20px;
    background: #1a2230;
    border: 1px solid rgba(255, 255, 255, 0.07);
    border-radius: 14px;
    transition: transform 0.25s, border-color 0.25s, box-shadow 0.25s;

    &.is-link {
      cursor: pointer;

      &:hover {
        transform: translateY(-4px);
        border-color: rgba(252, 110, 62, 0.6);
        box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4),
          0 0 0 3px rgba(252, 110, 62, 0.08);

        .visit-tip {
          color: #ff8b60;
        }
      }
    }

    .card-top {
      display: flex;
      align-items: center;
      gap: 12px;

      .monogram {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 42px;
        height: 42px;
        border-radius: 10px;
        font-size: 19px;
        font-weight: 700;
        color: #fff;
      }

      .card-info {
        flex: 1;
        min-width: 0;

        .card-name {
          overflow: hidden;
          font-size: 15px;
          font-weight: 600;
          color: #f0f3f8;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .card-host {
          overflow: hidden;
          margin-top: 4px;
          font-family: Consolas, Menlo, monospace;
          font-size: 12px;
          color: #6f7a8d;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }

      .qrcode-thumb {
        flex-shrink: 0;
        width: 42px;
        height: 42px;
        padding: 3px;
        background: #fff;
        border-radius: 8px;
        cursor: pointer;

        img {
          display: block;
          width: 100%;
          height: 100%;
          border-radius: 5px;
          object-fit: cover;
        }
      }
    }

    .card-desc {
      display: -webkit-box;
      flex: 1;
      overflow: hidden;
      min-height: 42px;
      margin-top: 14px;
      font-size: 13px;
      line-height: 1.6;
      color: #98a2b3;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
    }

    .card-actions {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 14px;
      padding-top: 12px;
      border-top: 1px solid rgba(255, 255, 255, 0.06);

      .visit-tip {
        font-size: 12px;
        color: #7d8799;
        transition: color 0.25s;

        &--muted {
          color: #5d6778;
        }
      }

      .copy-btn {
        padding: 4px;
        font-size: 14px;
        color: #66707f;
        cursor: pointer;
        transition: color 0.2s;

        &:hover {
          color: #ff8b60;
        }
      }
    }
  }
</style>
